import { queryOptions } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type {
  Account,
  InviteMemberInput,
  Membership,
  Project,
  SecretKey,
  SecretStatus,
  SetAccountPasswordInput,
  UpdateMembershipInput,
  WorkspaceSettings,
  WorkspaceSettingsPatch,
} from './types';

/**
 * Transport for the M1 settings screens.
 *
 * ── ASSUMED CONTRACT ──────────────────────────────────────────────────────────
 *   GET/PATCH  /workspaces/:workspaceId/settings
 *   GET          /workspaces/:workspaceId/settings/secrets
 *   PATCH/DELETE /workspaces/:workspaceId/settings/secrets/:key
 *   GET/POST     /workspaces/:workspaceId/people
 *   PATCH        /workspaces/:workspaceId/people/:membershipId
 *   GET/POST   /workspaces/:workspaceId/projects
 *   PATCH      /workspaces/:workspaceId/projects/:id
 *   DELETE     /workspaces/:workspaceId/projects/:id            (archives)
 *
 * All are gated `workspace.admin` server-side (people's GET/POST/PATCH
 * additionally need `internal.read`, satisfied by every Noorix role already).
 * A client role never reaches any of this — see requests/api.ts's note that the
 * server is the actual authority regardless of what the client asserts.
 *
 * Query keys are local to this feature, per the file-ownership split for this
 * milestone: `['ws', workspaceId, 'settings', …]`, matching the `['ws', id, …]`
 * prefix rule in lib/queryKeys.ts without editing that shared file. The lead
 * folds these into queryKeys.ts when this module is wired into app.module.ts.
 * ────────────────────────────────────────────────────────────────────────────
 */

function base(workspaceId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}`;
}

export const settingsKeys = {
  workspace: (workspaceId: string) => ['ws', workspaceId, 'settings', 'workspace'] as const,
  secrets: (workspaceId: string) => ['ws', workspaceId, 'settings', 'secrets'] as const,
  people: (workspaceId: string) => ['ws', workspaceId, 'settings', 'people'] as const,
  /**
   * One person's sign-in state. A sibling of `people` rather than a child,
   * because the reverse nesting is what would hurt: under the `people` prefix
   * this would be refetched by every role change and every deactivation on the
   * list, none of which can alter how anybody signs in. It is fetched only
   * while a row is expanded, so those refetches would all be for nothing.
   */
  account: (workspaceId: string, membershipId: string) =>
    ['ws', workspaceId, 'settings', 'account', membershipId] as const,
  projects: (workspaceId: string) => ['ws', workspaceId, 'settings', 'projects'] as const,
} as const;

// --- Workspace settings ------------------------------------------------------

export function fetchWorkspaceSettings(
  workspaceId: string,
  signal?: AbortSignal,
): Promise<WorkspaceSettings> {
  return api.get<WorkspaceSettings>(`${base(workspaceId)}/settings`, signal);
}

export function updateWorkspaceSettings(
  workspaceId: string,
  patch: WorkspaceSettingsPatch,
): Promise<WorkspaceSettings> {
  return api.patch<WorkspaceSettings>(`${base(workspaceId)}/settings`, patch);
}

export function workspaceSettingsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: settingsKeys.workspace(workspaceId),
    queryFn: ({ signal }) => fetchWorkspaceSettings(workspaceId, signal),
  });
}

// --- Secrets -------------------------------------------------------------------

export function fetchSecrets(workspaceId: string, signal?: AbortSignal): Promise<SecretStatus[]> {
  return api.get<SecretStatus[]>(`${base(workspaceId)}/settings/secrets`, signal);
}

/**
 * Never returns the value — the response is a status, same as every read of a
 * secret. PATCH, not PUT: `lib/api.ts`'s shared client exposes
 * get/post/patch/delete only, and this route is defined to match it (see
 * settings.controller.ts on the API side).
 */
export function setSecret(workspaceId: string, key: SecretKey, value: string): Promise<SecretStatus> {
  return api.patch<SecretStatus>(`${base(workspaceId)}/settings/secrets/${encodeURIComponent(key)}`, {
    value,
  });
}

export function removeSecret(workspaceId: string, key: SecretKey): Promise<SecretStatus> {
  return api.delete<SecretStatus>(`${base(workspaceId)}/settings/secrets/${encodeURIComponent(key)}`);
}

export function secretsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: settingsKeys.secrets(workspaceId),
    queryFn: ({ signal }) => fetchSecrets(workspaceId, signal),
  });
}

// --- People ----------------------------------------------------------------

export function fetchMemberships(workspaceId: string, signal?: AbortSignal): Promise<Membership[]> {
  return api.get<Membership[]>(`${base(workspaceId)}/people`, signal);
}

export function inviteMember(workspaceId: string, input: InviteMemberInput): Promise<Membership> {
  return api.post<Membership>(`${base(workspaceId)}/people`, input);
}

export function updateMembership(
  workspaceId: string,
  membershipId: string,
  patch: UpdateMembershipInput,
): Promise<Membership> {
  return api.patch<Membership>(`${base(workspaceId)}/people/${encodeURIComponent(membershipId)}`, patch);
}

export function membershipsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: settingsKeys.people(workspaceId),
    queryFn: ({ signal }) => fetchMemberships(workspaceId, signal),
  });
}

// --- Accounts ----------------------------------------------------------------
//
// How one person signs in, hung off their membership. Three POSTs rather than a
// PATCH because each is a named act with its own consequence and its own line
// in the audit trail — see AccountsController for why the route is shaped this
// way rather than as `/users/:id`.

function accountPath(workspaceId: string, membershipId: string): string {
  return `${base(workspaceId)}/people/${encodeURIComponent(membershipId)}/account`;
}

export function fetchAccount(
  workspaceId: string,
  membershipId: string,
  signal?: AbortSignal,
): Promise<Account> {
  return api.get<Account>(accountPath(workspaceId, membershipId), signal);
}

export function setAccountPassword(
  workspaceId: string,
  membershipId: string,
  input: SetAccountPasswordInput,
): Promise<Account> {
  return api.post<Account>(`${accountPath(workspaceId, membershipId)}/password`, input);
}

export function suspendAccount(workspaceId: string, membershipId: string, reason: string): Promise<Account> {
  return api.post<Account>(`${accountPath(workspaceId, membershipId)}/suspend`, { reason });
}

export function restoreAccount(workspaceId: string, membershipId: string): Promise<Account> {
  return api.post<Account>(`${accountPath(workspaceId, membershipId)}/restore`);
}

export function accountQueryOptions(workspaceId: string, membershipId: string) {
  return queryOptions({
    queryKey: settingsKeys.account(workspaceId, membershipId),
    queryFn: ({ signal }) => fetchAccount(workspaceId, membershipId, signal),
  });
}

// --- Projects ----------------------------------------------------------------

export function fetchProjects(workspaceId: string, signal?: AbortSignal): Promise<Project[]> {
  return api.get<Project[]>(`${base(workspaceId)}/projects`, signal);
}

export function createProject(workspaceId: string, name: string): Promise<Project> {
  return api.post<Project>(`${base(workspaceId)}/projects`, { name });
}

export function renameProject(workspaceId: string, id: string, name: string): Promise<Project> {
  return api.patch<Project>(`${base(workspaceId)}/projects/${encodeURIComponent(id)}`, { name });
}

export function setProjectArchived(workspaceId: string, id: string, archived: boolean): Promise<Project> {
  return api.patch<Project>(`${base(workspaceId)}/projects/${encodeURIComponent(id)}`, { archived });
}

export function projectsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: settingsKeys.projects(workspaceId),
    queryFn: ({ signal }) => fetchProjects(workspaceId, signal),
  });
}
